import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { BaseProjectIdProps } from "@custom-types/base-types";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { snapshotsBeingCreatedCountSelector } from "@store/projects/projects-selector";
import {
  fetchProjectSnapshots,
  fetchSnapshotsBeingCreatedCount,
} from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useEffect, useRef } from "react";

/** Interval in milliseconds to poll the progress API for snapshots being created */
const POLLING_INTERVAL = 10_000;

/**
 * Polls the progress API to track how many snapshots are currently being created for a project
 * and updates the store with the count.
 *
 * - Only users with permission to view the project cloud activity fetch the data.
 * - Polls at a regular interval while the snapshots tab is mounted.
 * - Reloads the snapshots table once whenever the number of snapshots being created decreases,
 *   so newly created snapshots show up in the table.
 */
export function useSnapshotsBeingCreatedTracker({
  projectId,
}: BaseProjectIdProps): void {
  const dispatch = useAppDispatch();
  const progressApiClient = useProgressApiClient({ projectId });
  const { canViewProjectCloudActivity } =
    useHasUserValidPermissionProjectLevel();
  const snapshotsBeingCreatedCount = useAppSelector(
    snapshotsBeingCreatedCountSelector
  );

  const previousCountRef = useRef(snapshotsBeingCreatedCount);

  // Reload the snapshots table once whenever the number of snapshots being created decreases
  useEffect(() => {
    if (snapshotsBeingCreatedCount < previousCountRef.current) {
      void dispatch(fetchProjectSnapshots({ projectId }));
    }
    previousCountRef.current = snapshotsBeingCreatedCount;
  }, [snapshotsBeingCreatedCount, dispatch, projectId]);

  // Poll the progress API while the user is allowed to view the project cloud activity
  useEffect(() => {
    if (!canViewProjectCloudActivity) {
      return;
    }

    function fetchCount(): void {
      void dispatch(fetchSnapshotsBeingCreatedCount({ progressApiClient }));
    }

    fetchCount();
    const intervalId = setInterval(fetchCount, POLLING_INTERVAL);

    return () => clearInterval(intervalId);
  }, [canViewProjectCloudActivity, dispatch, progressApiClient]);
}
